import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

import { NavLinkTabs } from "@/newComponents/NavLinkTabs";
import { cn } from "@/shadcn/lib/utils";

type Tab = {
  label: string;
  href: string;
};

export type PageLayoutProps = {
  title?: string;
  subtitle?: string | React.ReactNode;
  tabs?: Tab[];
  /** "compact" tightens tab pill spacing so wide tab sets fit on desktop without scrolling. */
  tabsDensity?: "default" | "compact";
  /** Hides the sticky top header (title/subtitle/tabs). */
  hideHeader?: boolean;
  /** Render node in the top-right of the title row (e.g., export button, CTA) */
  actions?: React.ReactNode;
  /** Centers/constrains the title row when main content uses a narrower max width than the shell. */
  headerContentClassName?: string;
  /** Shows a "< label" back link above the title. Use on detail pages instead of tabs. */
  backLink?: { label: string; href: string };
  /** Render node inside the sticky header, above the title row (e.g., search bar). */
  topBar?: React.ReactNode;
  children: React.ReactNode;
  theme?: "ageless" | "repeatmd";
  width?: "full" | "narrow" | "wide";
};

/** Props for {@link PageLayoutAgeless}; the same shape as the default export's. */
export type PageLayoutAgelessProps = PageLayoutProps;

const PageLayout = ({
  subtitle,
  title,
  tabs,
  tabsDensity = "default",
  hideHeader = false,
  actions,
  headerContentClassName,
  backLink,
  topBar,
  children,
  theme,
  width = "full",
}: PageLayoutProps): React.ReactElement => {
  const { pathname } = useLocation();
  const isAgelessRoute = /\/ageless[-/]/.test(pathname);

  const hasTabs = !!tabs?.length;

  const inner = (
    <div
      className={cn(
        "container mx-auto h-full w-full border-border px-6 lg:px-8",
        { full: "max-w-7xl", narrow: "max-w-4xl", wide: "max-w-none" }[width],
      )}
      data-testid="PageLayoutAgeless"
      data-theme={theme ?? (isAgelessRoute ? "ageless" : "repeatmd")}
    >
      <div className="flex h-full w-full flex-col">
        {!hideHeader && (
          <div>
            {topBar}
            <div className={cn("mb-3 py-4 lg:mb-6 lg:pt-8")}>
              {backLink && (
                <div className="h-6">
                  <Link
                    className="inline-flex items-center gap-1 type-label-sm text-secondary transition-colors hover:text-primary"
                    to={backLink.href}
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {backLink.label}
                  </Link>
                </div>
              )}

              {/* Title row */}
              <div
                className={cn(
                  "flex flex-col items-start gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4",
                  headerContentClassName,
                )}
              >
                <div className="flex flex-col gap-1.5">
                  <h1 className="type-heading-xl">{title}</h1>
                  {subtitle != null &&
                    subtitle !== "" &&
                    (typeof subtitle === "string" ? (
                      <span className="type-body-md">{subtitle}</span>
                    ) : (
                      subtitle
                    ))}
                </div>
                {actions && <div className="w-full sm:w-auto sm:shrink-0">{actions}</div>}
              </div>

              {/* NavLinkTabs (section pages) */}
              {hasTabs && (
                <div className="mt-3">
                  <NavLinkTabs
                    density={tabsDensity}
                    items={tabs.map((tab) => ({
                      children: tab.label,
                      end: true,
                      to: tab.href,
                    }))}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Scrollable content */}
        <div className={cn("relative min-h-0 grow pb-8")}>
          <div className={cn("min-w-0 pb-6")}>{children}</div>
        </div>
      </div>
    </div>
  );

  if (isAgelessRoute) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          animate={{ opacity: 1 }}
          className="min-h-screen w-full"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          key={pathname}
          // style={{ backgroundColor: "#f5f5f5" }}
          transition={{ duration: 0.2 }}
        >
          {inner}
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        key={pathname}
        transition={{ duration: 0.2 }}
      >
        {inner}
      </motion.div>
    </AnimatePresence>
  );
};

export default PageLayout;

/**
 * Named export for `@/newComponents`. The barrel already exports a different `PageLayout`
 * (the breadcrumb shell), so this shell keeps its established Ageless name there.
 */
export const PageLayoutAgeless = PageLayout;
